import React from "react";
import { Bluetooth } from "lucide-react";
import BikeDashboard from "./BikeDashboard";
import RowerDashboard from "./RowerDashboard";
import { DashboardTransport, PauseButton } from "./dashboardWidgets";
import DashboardTimingHeader from "./DashboardTimingHeader";

/**
 * Polymorphic secondary pane. Picks the right concrete dashboard for the
 * currently-active device, or renders a "no device" empty state when nothing
 * is paired. New equipment types are wired in by adding a case here and
 * implementing the matching dashboard component.
 */
const EquipmentDashboard = ({
  device,
  onAddDevice,
  timing,
  hr,
  hrBpm,
  isPaused,
  isRunning,
  onPlayPause,
  pauseMenu,
}) => {
  if (!device || !device.type) {
    return (
      <div className="relative flex flex-col items-center justify-center h-full max-w-lg mx-auto p-6 gap-4 text-white text-center">
        <div className="absolute inset-x-4 top-4">
          <DashboardTimingHeader
            {...timing}
            action={
              onPlayPause && !isPaused ? (
                <PauseButton isRunning={isRunning} onPlayPause={onPlayPause} />
              ) : null
            }
          />
        </div>
        <Bluetooth className="w-10 h-10 text-white/40" />
        <h2 className="text-lg font-semibold">No equipment connected</h2>
        <p className="text-sm text-white/60 max-w-sm">
          Pair an FTMS-compatible bike or rower to stream live power into your
          workout.
        </p>
        <button
          type="button"
          onClick={onAddDevice}
          className="mt-2 rounded-full border border-white/30 px-4 py-2 text-sm text-white/80 hover:text-white hover:border-white/60"
        >
          Connect equipment
        </button>
        <DashboardTransport
          isPaused={isPaused}
          isRunning={isRunning}
          onPlayPause={onPlayPause}
          timing={timing}
          pauseMenu={pauseMenu}
        />
      </div>
    );
  }

  const common = {
    subscribe: device.subscribe,
    metricsRef: device.metricsRef,
    summaryRef: device.summaryRef,
    status: device.status,
    deviceName: device.name,
    onAddDevice,
    timing,
    hr,
    hrBpm,
    isPaused,
    isRunning,
    onPlayPause,
    pauseMenu,
  };

  switch (device.type) {
    case "indoor_bike":
      return <BikeDashboard {...common} />;
    case "rower":
      return <RowerDashboard {...common} />;
    default:
      // Phase 2 types fall back to the bike dashboard for now — replace with
      // type-specific dashboards as they come online.
      return <BikeDashboard {...common} />;
  }
};

export default EquipmentDashboard;
